import { NumberMaterialInput } from 'components/Custom/NumberMaterialInput'
import { Box, Slider } from 'components/Material/index'
import { useEffect, useState } from 'react'
import { Control, Controller, FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form'

interface DoubleFloatNumberInputWithSliderProps<T extends FieldValues> {
  control: Control<T>
  name: string
  label: string
  getValues: (name: string) => number
  setValue: UseFormSetValue<T>
  minValue?: number
  maxValue?: number
  adornementText?: string
  auxLabel?: string
}

export const DoubleFloatNumberInputWithSlider = <T extends FieldValues>({
  control,
  getValues,
  setValue,
  name,
  label,
  minValue = 0,
  maxValue = 1000000,
  auxLabel = '',
  adornementText
}: DoubleFloatNumberInputWithSliderProps<T>) => {
  const nameInicial = name + '_inicial'
  const nameFinal = name + '_final'

  const currentValueInicial = getValues(nameInicial)
  const currentValueFinal = getValues(nameFinal)
  return (
    <Box className="flex flex-col gap-5">
      <Box className="label-large">{label}</Box>
      <Box className="flex gap-5">
        <ControllerInputField<T>
          control={control}
          name={nameInicial as Path<T>}
          label={auxLabel ? 'Mínimo ' + auxLabel : 'Mínimo'}
          minValue={minValue}
          maxValue={maxValue}
          adornementText={adornementText}
          currentValueInicial={currentValueInicial}
          currentValueFinal={currentValueFinal}
        />

        <ControllerInputField<T>
          control={control}
          name={nameFinal as Path<T>}
          label={auxLabel ? 'Máximo ' + auxLabel : 'Máximo'}
          minValue={minValue}
          maxValue={maxValue}
          adornementText={adornementText}
          currentValueInicial={currentValueInicial}
          currentValueFinal={currentValueFinal}
        />
      </Box>
      <Box className="flex justify-center px-5 pt-4">
        <RangeSlider
          getValues={getValues}
          setValue={setValue}
          name={name}
          minValue={minValue}
          maxValue={maxValue}
        />
      </Box>
    </Box>
  )
}

type ControllerInputFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  minValue: number
  maxValue: number
  adornementText?: string
  currentValueInicial: number
  currentValueFinal: number
}

function ControllerInputField<T extends FieldValues>({
  control,
  name,
  label,
  maxValue,
  minValue,
  adornementText,
  currentValueInicial,
  currentValueFinal
}: ControllerInputFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <NumberMaterialInput
            {...field}
            value={field.value}
            label={label}
            fullWidth
            adornment={adornementText}
            max={maxValue}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              const rawValue = e.target.value.replace(/[^0-9]/g, '')
              const numericValue = parseFloat(rawValue) / 100
              const isInvalidValue = numericValue < minValue || numericValue > maxValue
              if (name.includes('inicial')) {
                if (isInvalidValue || numericValue > currentValueFinal) field.onChange(minValue)
              } else if (name.includes('final')) {
                if (isInvalidValue || numericValue < currentValueInicial) field.onChange(maxValue)
              }
            }}
          />
        )
      }}
    />
  )
}

type RangeSliderProps<T extends FieldValues> = {
  getValues: (name: string) => number
  setValue: UseFormSetValue<T>
  name: string
  minValue?: number
  maxValue?: number
}
function RangeSlider<T extends FieldValues>({
  getValues,
  setValue,
  name,
  minValue = 0,
  maxValue = 1000000
}: RangeSliderProps<T>) {
  const nameInicial = (name + '_inicial') as Path<T>
  const nameFinal = (name + '_final') as Path<T>
  const valueInitial = getValues(nameInicial)
  const valueFinal = getValues(nameFinal)
  const [value, setValueState] = useState([valueInitial, valueFinal])

  useEffect(() => {
    const updateValues = () => {
      const updatedInitial = getValues(nameInicial)
      const updatedFinal = getValues(nameFinal)
      setValueState([updatedInitial, updatedFinal])
    }
    updateValues()
  }, [valueInitial, valueFinal])
  const handleChange = (_: Event, newValue: number | number[]) => {
    const newValues = newValue as PathValue<T, Path<T>>
    setValueState(newValues)
    setValue(nameInicial, newValues[0])
    setValue(nameFinal, newValues[1])
  }

  return (
    <Slider
      value={value}
      onChange={handleChange}
      valueLabelDisplay="auto"
      min={minValue}
      max={maxValue}
    />
  )
}
